import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import { ButtonProps } from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import React from 'react'
import ActionButton, { ActionButtonProps } from './ActionButton.tsx'

/**
 * Props for the CustomDialog component.
 */
interface CustomDialogProps {
  /**
   * Indicates whether the dialog is open or not.
   * @default false
   */
  isOpen?: boolean

  /**
   * The icon to display in the header section of the dialog. If undefined,
   * the dialog will have no icon.
   * @default undefined
   */
  icon?: React.JSX.Element

  /**
   * The function to call when the dialog is closed. If undefined, the dialog
   * will not have a close button in the top right corner. Use this to make necessary
   * state changes when the dialog is closed.
   * @default undefined
   */
  onClose?: () => void

  /**
   * The title of the dialog. If undefined, the dialog will have no title.
   * @default undefined
   */
  title?: string

  /**
   * The body of the dialog. If undefined, the dialog will have no body.
   */
  body?: React.JSX.Element

  /**
   * The possible actions, as buttons, displayed at the bottom of the dialog.
   *
   * The final action button will be designated the primary action button, and
   * all others are secondary (if any). If empty, the dialog will have no
   * actions section.
   * @default []
   */
  actions?: ActionButtonProps[]

  /**
     * The props for the primary action button to determine the variant and
     * color of the button. The primary action button is the final button in the
     * actions array.
     @default undefined
     */
  primaryButtonTheme?: ButtonThemeProps

  /**
   * The props for the secondary action button(s) to determine the variant and
   * color of the button(s).
   *
   * When there is more than one action button, the final button in the
   * actions array is primary and all others are secondary, unless there is
   * only one action button, in which case it is the primary action button and
   * there are no secondary action buttons.
   * @default undefined
   */
  secondaryButtonTheme?: ButtonThemeProps
}

/**
 * Props defining the variant and color of the ActionButtonProps in the
 * CustomDialog component.
 */
interface ButtonThemeProps {
  /**
   * The variant of the button. If undefined, the final (primary) button will
   * use the default 'contained' variant, and the other (secondary) buttons
   * (if any) will use the 'outlined' variant.
   * @default undefined
   */
  variant?: ButtonProps['variant']

  /**
   * The color of the button. If undefined, the final (primary) button will
   * use the default 'primary' color, and the other (secondary) buttons (if
   * any) will use the 'secondary' color.
   * @default undefined
   */
  color?: ButtonProps['color']
}

const DEFAULT_PRIMARY_VARIANT: ButtonThemeProps['variant'] = 'contained'
const DEFAULT_SECONDARY_VARIANT: ButtonThemeProps['variant'] = 'outlined'

const DEFAULT_PRIMARY_COLOR: ButtonThemeProps['color'] = 'primary'
const DEFAULT_SECONDARY_COLOR: ButtonThemeProps['color'] = 'secondary'

/**
 * CustomDialog is a custom dialog that can be used to prompt the user to take
 * an action. It consists of these sections:
 *
 * 1. The header section at the top, which contains the icon and the close button,
 *    if any.
 * 2. The title of the dialog, if any.
 * 3. The body of the dialog, if any.
 * 4. The action buttons section at the bottom, if any. The final action button in the
 *    array will be styled as the primary action. All other buttons (if any) will be
 *    styled as secondary actions.
 *
 * This component largely wraps the MUI Dialog component, but adds some customizations
 * and consistencies.
 * @param {CustomDialogProps} props - The props for the CustomDialog component.
 * @constructor
 */
const CustomDialog: React.FC<CustomDialogProps> = ({
  isOpen = false,
  icon,
  onClose,
  title,
  body,
  actions = [],
  primaryButtonTheme,
  secondaryButtonTheme,
}: CustomDialogProps) => {
  /**
   * getVariant returns the variant of the button based on its index in the
   * actions array. The last button in the array is the primary action button,
   * and the rest are secondary action buttons.
   * @param {number} idx - The index of the button in the actions array.
   */
  const getVariant = React.useCallback(
    (idx: number) => {
      return idx === actions.length - 1
        ? primaryButtonTheme?.variant || DEFAULT_PRIMARY_VARIANT
        : secondaryButtonTheme?.variant || DEFAULT_SECONDARY_VARIANT
    },
    [actions.length, primaryButtonTheme, secondaryButtonTheme],
  )

  /**
   * getColor returns the color of the button based on its index in the
   * actions array. The last button in the array is the primary action button,
   * and the rest are secondary action buttons.
   * @param {number} idx - The index of the button in the actions array.
   */
  const getColor = React.useCallback(
    (idx: number) => {
      return idx === actions.length - 1
        ? primaryButtonTheme?.color || DEFAULT_PRIMARY_COLOR
        : secondaryButtonTheme?.color || DEFAULT_SECONDARY_COLOR
    },
    [actions.length, primaryButtonTheme, secondaryButtonTheme],
  )

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="custom-dialog-title"
      aria-describedby="custom-dialog-description"
    >
      {(icon || onClose) && ( // compress layout if no icon or close button
        <Box display="flex" justifyContent="center" alignItems="center">
          <Box m="1rem 0 2rem">{icon}</Box>
          {onClose && (
            <IconButton
              onClick={onClose}
              sx={{ position: 'absolute', right: 8, top: 8 }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}

      {title && (
        <DialogTitle id="custom-dialog-title" variant="h5">
          {title}
        </DialogTitle>
      )}

      <DialogContent>
        {' '}
        {/* always need DialogContent or DialogActions misalign */}
        {body && <DialogContentText id="custom-dialog-description">{body}</DialogContentText>}
      </DialogContent>
      {actions && actions.length > 0 && (
        <DialogActions>
          {actions.map((action, idx) => (
            <ActionButton
              key={action.label}
              variant={getVariant(idx)}
              color={getColor(idx)}
              {...action}
            />
          ))}
        </DialogActions>
      )}
    </Dialog>
  )
}

export type { ButtonThemeProps, CustomDialogProps }

export default CustomDialog
